import React, { useRef, useState } from 'react';
import { ContactsHeader } from './Header';
import { ContactsRow } from './Row';
import { useSearchParams } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { EllipsisDropdownPortal, RecordsTable, USER_PERMISSIONS, PRODUCT_PERMISSIONS, Notification, STATUS_TYPES } from 'dyl-components';
import SequenceModal from 'shared/modals/SequenceModal';
import QuoteBuilderProvider from 'shared/context/QuoteBuilderProvider';
import QuoteBuilderModal from 'shared/modals/QuoteBuilderModal';
import contactsActions from 'actions/contacts';
import { Dropdown, Icon, Ref } from 'semantic-ui-react';
import RestrictedView from 'shared/RestrictedView';

const ContactsRowContainer = ({ contact, isColumnHidden, onSelectContactForConversion, refresh, selectedRow, onEditContactSequence, onCloseEditContactSequence, onMarkAsDirectCustomer }) => {
    const ref = useRef();

    return (
        <ContactsRow 
            key={contact.id} 
            contact={contact} 
            isColumnHidden={isColumnHidden} 
            onSelectContactForConversion={() => onSelectContactForConversion(contact.id)} 
            refresh={refresh}
            actions={(
                <>
                    <Ref innerRef={ref}>
                        <EllipsisDropdownPortal reference={ref}>
                            <RestrictedView type="disabled" userPermission={USER_PERMISSIONS.CONTACTS_EDIT} userRestrict assignmentIds={[(contact?.owner_id || 0), (contact?.coowner_id || 0)]}>
                                <Dropdown.Item onClick={onEditContactSequence} text={<React.Fragment>
                                    <Icon name={'fas fa-sitemap'} />
                                    Sequences
                                </React.Fragment>}
                                    style={{ cursor: 'pointer' }}
                                />
                            </RestrictedView>
                            <Dropdown.Item onClick={onMarkAsDirectCustomer} text={<>
                                    <Icon name={'fas fa-crown'} />
                                    {contact?.direct_customer ? 'Unmark as' : 'Mark as'} Direct Customer
                                </>}
                                    style={{ cursor: 'pointer' }}
                                />
                        </EllipsisDropdownPortal>
                    </Ref>
                    <SequenceModal 
                        isModalOpen={selectedRow === contact.id} 
                        modalRef={ref} 
                        onClose={onCloseEditContactSequence} 
                        contact_id={contact.id} 
                        refresh={refresh} 
                        owner_id={contact?.owner_id}
                    />
                </>
            )}
        />
    )
}

const ContactsTable = ({ onFilter, isColumnHidden, onPageChange, onOpenAddContactModal, readAccounts }) => {
    const { count, contacts = [], isReadingContacts } = useSelector(state => state.contacts);
    const { isReadingOwnersAndCoowners } = useSelector(state => state.contact);
    const contactsData = contacts.map(contact => {
        const first_name = contact.first_name || '';
        const last_name = contact.last_name || '';
        const suffix = contact.suffix || '';

        return {
            ...contact,
            account_id: contact.account ? contact.account.id : 0,
            first_name,
            last_name,
            suffix,
            phone: [contact.phone]
        }
    });

    const [params] = useSearchParams();
    const [selectedRow, setSelectedRow] = useState(null);

    const [contactBeingConverted, setContactBeingConverted] = useState(null);
    
    const onSelectContactForConversion = (id) => {
        setContactBeingConverted(id);
    }

    const detailsOfContactBeingConverted = contacts.find(contact => contact.id === contactBeingConverted);
    const dispatch = useDispatch();

    const refresh = () => {
        const query = Object.fromEntries(params);
        return dispatch(contactsActions.readContacts({ ...query, limit: 25 }));
    }

    const onMarkAsDirectCustomer = async (contact) => {
        if (!contact || !contact?.id) return;
        const action = contact?.direct_customer
            ? "unset_direct_customer"
            : "set_direct_customer";
        await dispatch(
            contactsActions.contactBulkAction({
                action: action,
                contact_id: [contact.id],
            }),
        );
        Notification.alert(
            `Successfully ${contact?.direct_customer === true ? "unmarked" : "marked"} as Direct Customer`,
            STATUS_TYPES.SUCCESS,
        );
        readAccounts?.();
    };

    return (
        <QuoteBuilderProvider refreshAccountInformation={refresh}>
            <RecordsTable
                tableBodyClassName='ContactsTable'
                isTableLoading={isReadingContacts || isReadingOwnersAndCoowners}
                isSearchResultEmpty={count === 0 && (params.get('search') || params.get('start') || params.get('end') || params.get('account_type') || params.get('owned_by'))}
                isTableEmpty={count === 0 && !(params.get('search') || params.get('start') || params.get('end') || params.get('account_type') || params.get('owned_by'))}
                TableHeader={<ContactsHeader isColumnHidden={isColumnHidden} onFilter={onFilter} />}
                TableRows={contactsData.map(contact => (
                    <ContactsRowContainer
                        key={contact.id} 
                        contact={contact} 
                        isColumnHidden={isColumnHidden} 
                        onSelectContactForConversion={() => onSelectContactForConversion(contact.id)} 
                        refresh={refresh}
                        setSelectedRow={setSelectedRow}
                        selectedRow={selectedRow}
                        onCloseEditContactSequence={() => setSelectedRow(null)}
                        onEditContactSequence={() => setSelectedRow(contact.id)}
                        onMarkAsDirectCustomer={() => onMarkAsDirectCustomer(contact)}
                    />
                ))}
                recordsCount={count}
                activePage={params.get('page')}
                onPageChange={onPageChange}
                recordsName='Contact'
                onClick={onOpenAddContactModal}
                userPermission={USER_PERMISSIONS.CONTACTS_ADD}
                productPermission={PRODUCT_PERMISSIONS.RECORDS}
            />
            <QuoteBuilderModal
                contact_id={detailsOfContactBeingConverted?.id} 
                account_id={detailsOfContactBeingConverted?.account?.id} 
                isLinkedToCustomer={detailsOfContactBeingConverted?.account?.customer} 
                pipeline_id={detailsOfContactBeingConverted?.module?.id} 
            />
        </QuoteBuilderProvider>
    )
}

export default ContactsTable;
